import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { bookingService, talentService, emailService } from '../../api/apiService';
import { lanoService } from '../../api/lanoService';
import Select from 'react-select';
import { customStyles } from './../../helpers/StyleUtils';
import confirmIcon from '../../images/confirm.svg';
import declineIcon from '../../images/decline.svg';

const ListViewConfirmed = ({ events, initialWeek }) => {
    const [searchTerm, setSearchTerm] = useState("");
    const [selectedUser, setSelectedUser] = useState(null);
    const [isCreatingTask, setIsCreatingTask] = useState(false);
    const [processingBookings, setProcessingBookings] = useState(new Set());
    const navigate = useNavigate();
    const processedGroups = new Set();

    const handleChange = event => {
        setSearchTerm(event.target.value);
    };

    const fetchBooking = async (bookingId) => {
      if (!bookingId) {
        console.error("Invalid booking ID provided:", bookingId);
        return null;
      }
      
      try {
        const response = await bookingService.getBookingById(bookingId);
        
        // Handle different API response formats
        if (response.data) {
          // Case 1: { booking: {...} }
          if (response.data.booking) {
            return response.data.booking;
          }
          // Case 2: Direct booking data in response.data
          else if (response.data.id || response.data.start_date) {
            return response.data;
          }
        }
        
        console.error("Booking not found for ID:", bookingId);
        return null;
      } catch (error) {
        console.error("Error fetching booking:", error);
        return null;
      }
    };

    const fetchBookingsByGroupId = async (groupId) => {
      try {
        // Use search endpoint to find bookings with the same group ID and confirmed status
        const params = {
          group: groupId,
          status: 'confirmed'
        };
        const response = await bookingService.searchBookings(params);
        
        if (response.data && response.data.bookings) {
          return response.data.bookings;
        }
        return [];
      } catch (error) {
        console.error("Error fetching bookings by group:", error);
        return [];
      }
    };

  // Fetch talent based on talentId
  const fetchTalent = async (talentId) => {
    if (!talentId) {
      console.error("Invalid talent ID provided:", talentId);
      return null;
    }
    
    try {
      const response = await talentService.getTalentById(talentId);
      
      // Handle different API response formats
      if (response && response.data) {
        // Case 1: { talent: {...} }
        if (response.data.talent) {
          return response.data.talent;
        }
        // Case 2: Direct talent data in response.data
        else if (response.data.id || response.data.Name || response.data.E_Mail) {
          return response.data;
        }
      }
      
      console.error("Talent not found for ID:", talentId);
      return null;
    } catch (error) {
      console.error("Error fetching talent:", error);
      return null;
    }
  };
    
  const acceptporposedtalent = async (bookingId, skipConfirmation = false) => {
      // Check if this specific booking is already being processed
      if (processingBookings.has(bookingId)) {
        alert("This booking is already being processed. Please wait.");
        return;
      }
      
      // Check if any task is being created globally
      if (isCreatingTask) {
        alert("A task is already being processed. Please wait.");
        return;
      }
      
      if (!skipConfirmation) {
        const userConfirmed = window.confirm("Are you sure you want to confirm?");
        if (!userConfirmed) {
          // console.log("Operation cancelled.");
          return;
        }
      }
      
      // Mark this booking as being processed
      setProcessingBookings(prev => new Set(prev).add(bookingId));
      setIsCreatingTask(true);
      try {
        const booking = await fetchBooking(bookingId);
        let bookingsToUpdate = [];
  
        if (booking.group !== null && booking.group !== undefined && booking.group !== '') {
          const groupBookings = await fetchBookingsByGroupId(booking.group);
          bookingsToUpdate = groupBookings;
        } else {
          bookingsToUpdate.push(booking);
        }
  
        // Check the status of all bookings to ensure they are still confirmed
        bookingsToUpdate = await Promise.all(
          bookingsToUpdate.map(async (b) => {
            const currentBooking = await fetchBooking(b.id);
              if (!currentBooking || currentBooking.status !== 'confirmed') {
                // console.warn(`Booking ${b.id} status has changed or doesn't exist.`);
              return null; // Return null for bookings that are no longer confirmed
            }
            return currentBooking;
          })
        );
  
        // Filter out any null entries (bookings that are no longer confirmed)
        bookingsToUpdate = bookingsToUpdate.filter(b => b !== null);
  
        // If there are no bookings left to update, reload the page with an error
        if (bookingsToUpdate.length === 0) {
          alert("All bookings in the group have changed status. Please refresh the page.");
          window.location.reload();
          return;
        }
  
        // Proceed with the updates if all bookings are still confirmed
        for (const b of bookingsToUpdate) {
            const talentId = b.talent_id || b.talentId || b.talent || null;
            if (!talentId) {
              console.error("No talent ID found for booking:", b.id);
              continue;
            }
            
            
            const talent = await fetchTalent(talentId);
            if (!talent) {
              console.error("Talent not found for ID:", talentId);
              continue;
            }
            
            try {
              await createTask(b.id, b, talent); // Wait for the task creation to complete
    
              // After the task is created successfully:
              await updateBookingStatus(b.id, b); // Update the booking
              // console.log(b.id, b);
    
              // Send email notification
              await sendEmailNotification(b, talent);
            } catch (error) {
              // Handle error for individual booking
              console.error(`Error processing booking ${b.id}:  `, error);
              alert(`LANO: An error occurred while processing the booking ${b.id}. Please try again.`);
              setIsCreatingTask(false);
              window.location.reload(); // Reload the page on error
              return; // Exit the function to prevent further execution
            }
        }
  
        // If all tasks were created successfully and updates/emails were sent
        alert("Booking(s) updated and Lano task(s) created successfully.");
        window.location.reload();
      } catch (error) {
        console.error("Error processing booking(s): ", error);
        alert(`An error occurred: ${error.message}. Please try again.`);
        window.location.reload(); // Reload the page on error
      } finally {
        // Remove this booking from processing set and reset global flag
        setProcessingBookings(prev => {
          const newSet = new Set(prev);
          newSet.delete(bookingId);
          return newSet;
        });
        setIsCreatingTask(false);
      }
  };
  
  const updateBookingStatus = async (bookingId, booking) => {
    try {
      // Update the booking status using the API
      await bookingService.updateBookingStatus(bookingId, 'Booked');
      // console.log(`Successfully updated booking ${bookingId} to 'Booked'`);
    } catch (error) {
      console.error(`Error updating booking ${bookingId}:`, error);
      throw error; // Re-throw to handle in the calling function
    }
  };
    
  const declineproposedtalent = async (bookingId, skipConfirmation = false) => {
    // Check if this specific booking is already being processed
    if (processingBookings.has(bookingId)) {
      alert("This booking is already being processed. Please wait.");
      return;
    }
    
    if (!skipConfirmation) {
      const userConfirmed = window.confirm("Are you sure you want to reject this order?");
      if (!userConfirmed) {
        // console.log("Operation cancelled.");
        return;
      }
    }
    
    // Mark this booking as being processed
    setProcessingBookings(prev => new Set(prev).add(bookingId));
    
    try {
      const booking = await fetchBooking(bookingId);
      let bookingsToUpdate = [];

      if (booking.group) {
        const groupBookings = await fetchBookingsByGroupId(booking.group);
        bookingsToUpdate = groupBookings;
      } else {
        bookingsToUpdate.push(booking);
      }

      // Check the status of all bookings to ensure they are still confirmed
      bookingsToUpdate = await Promise.all(
        bookingsToUpdate.map(async (b) => {
          const currentBooking = await fetchBooking(b.id);
          if (!currentBooking || currentBooking.status !== 'confirmed') {
            // console.warn(`Booking ${b.id} status has changed or doesn't exist.`);
            return null; // Return null for bookings that are no longer confirmed
          }
          return currentBooking;
        })
      );

      // Filter out any null entries (bookings that are no longer confirmed)
      bookingsToUpdate = bookingsToUpdate.filter(b => b !== null);

      // If there are no bookings left to update, reload the page with an error
      if (bookingsToUpdate.length === 0) {
        alert("All bookings in the group have changed status. Please refresh the page.");
        window.location.reload();
        return;
      }

      // Proceed with the updates if all bookings are still confirmed
      for (const booking of bookingsToUpdate) {
        await bookingService.updateBookingStatus(booking.id, 'declined');
      }

      if (!skipConfirmation) {
        alert("Booking(s) declined successfully.");
        window.location.reload();
      }
    } catch (error) {
      console.error("Error declining booking(s): ", error);
      alert(`An error occurred: ${error.message}. Please try again.`);
      window.location.reload(); // Reload the page on error
    } finally {
      // Remove this booking from processing set
      setProcessingBookings(prev => {
        const newSet = new Set(prev);
        newSet.delete(bookingId);
        return newSet;
      });
    }
  };

    // Send email notification for confirmed booking
    const sendEmailNotification = async (booking, talent) => {
      try {
        // Format dates for email
        const startDate = typeof booking.start_date === 'string' ? new Date(booking.start_date) : 
                          booking.start_date && booking.start_date.seconds ? new Date(booking.start_date.seconds * 1000) : 
                          new Date();
        const endDate = typeof booking.end_date === 'string' ? new Date(booking.end_date) : 
                        booking.end_date && booking.end_date.seconds ? new Date(booking.end_date.seconds * 1000) : 
                        startDate;
        
        // Get client and position names
        const clientName = typeof booking.client === 'object' ? (booking.client.label || '') : (booking.client || '');
        const positionName = typeof booking.position === 'object' ? (booking.position.label || '') : (booking.position || '');
        
        // Get time information with fallbacks
        const startTime = booking.start_time || booking.startTime || booking.startTime || 'N/A';
        const endTime = booking.end_time || booking.endTime || booking.endTime || 'N/A';
        
        // Debug logging
        // console.log('Email time debugging:', {
          // bookingId: booking.id,
          // start_time: booking.start_time,
          // startTime: booking.startTime,
          // end_time: booking.end_time,
          // endTime: booking.endTime,
          // finalStartTime: startTime,
          // finalEndTime: endTime
        // });
        
        // Create email content
        const emailContent = `
          <h2>Booking Confirmation</h2>
          <p>Dear ${talent.name || talent.Name},</p>
          <p>Your booking has been confirmed with the following details:</p>
          <ul>
            <li><strong>Title:</strong> ${booking.title || booking.boktitle || 'N/A'}</li>
            <li><strong>Client:</strong> ${clientName}</li>
            <li><strong>Position:</strong> ${positionName}</li>
            <li><strong>Start Date:</strong> ${startDate.toLocaleDateString()}</li>
            <li><strong>End Date:</strong> ${endDate.toLocaleDateString()}</li>
            <li><strong>Time:</strong> ${startTime} - ${endTime} Please check the call sheet for your final call time</li>
            <li><strong>KPI:</strong> ${(() => {
              const kpiValue = booking.kpi;
              if (kpiValue === null || kpiValue === undefined || kpiValue === '') {
                return 'N/A';
              }
              
              // Handle objects (like location objects that might be in the wrong field)
              if (typeof kpiValue === 'object' && kpiValue !== null) {
                return 'N/A';
              }
              
              // Convert to number and round to integer
              const numericValue = parseFloat(kpiValue);
              if (!isNaN(numericValue)) {
                return Math.round(numericValue);
              }
              
              // If it's not a valid number, return 'N/A'
              return 'N/A';
            })()}</li>
            <li><strong>Rate:</strong> ${booking.custom_rate?.value || booking.customRate || talent.Tagessatz}</li>
            <li><strong>Location:</strong> ${(() => {
              const locationValue = booking.location;
              if (locationValue === null || locationValue === undefined || locationValue === '') {
                return 'N/A';
              }
              
              // Handle location as an object
              if (typeof locationValue === 'object' && locationValue !== null) {
                return locationValue.name || locationValue.short || 'N/A';
              }
              
              // Handle location as a string
              if (typeof locationValue === 'string') {
                return locationValue;
              }
              
              return 'N/A';
            })()}</li>
            ${booking.sonstige_info ? `<li><strong>Additional Info:</strong> ${booking.sonstige_info}</li>` : ''}
          </ul>


          <p>In case of illness or delay, please send an email directly to the distribution list planning@orendtstudios.com and krank@set-cast.com by 8:00 a.m. at the latest.</p>
          <p>Please make sure to inform Set-Cast and the producer of the production if you need to leave the set early and double check with the producer if the KPI has been reached.</p>
          <p>For questions regarding KPI, shooting schedules, callsheets and briefings, please contact planning@orendtstudios.com.</p>

    <p style="font-size:16px; font-weight:bold; margin:0 0 10px 0;">
            Invoice Details
          </p>

          <p style="font-size:16px; font-weight:bold; color:#c00000; margin:0 0 15px 0;">
            ATTENTION: New address as of January&nbsp;1,&nbsp;2026
          </p>

          <p style="font-size:14px; margin:0 0 10px 0;">
            Please use the following address for all invoices with immediate effect:
          </p>

          <p style="font-size:14px; margin:0; line-height:1.6;">
            <strong>Set Cast CTA GmbH</strong><br>
            Essener Bogen&nbsp;5<br>
            22419 Hamburg
          </p>


          <p>Invoice details:</p>
          <p>please upload your invoice in Lano - you could combine several tasks in one invoice</p>
          <p>If you have any questions, please contact us at booking@set-cast.com</p>
          <p>Best regards,<br>The Set-Cast Team</p>
          
          <p>The agreed daily rate covers all costs and incidental expenses associated with the provision of the contractually agreed services. These include, in particular, but are not limited to, travel expenses, accommodation costs, meal expenses, and all other expenses incurred in the course of providing the services. No additional costs will be incurred by the client</p>

          <p>LEGENDE:</p>
          <p>B28 = Behringstrasse 28a, 22765 Hamburg</p>
          <p>TR4 = Tarpenring 4, 22419 Hamburg</p>
          <p>M= Holy-Allee 3, Metzingen , am Empfang melden</p>
          <p>OSB= Am Rosenkothen 2, 40880 Ratingen</p>
            <p>EB5 = Essener Bogen 5, 22419 Hamburg</p>
        `;

        const emailData = {
       
          to_email: ['booking@set-cast.com','talents@set-cast.com', talent.email || talent.E_Mail],

          subject: 'Booking Confirmation - Set-Cast',
          content: emailContent,
          template_data: {
            talent_name: talent.name || talent.Name,
            booking_title: booking.title || booking.boktitle,
            start_date: startDate.toLocaleDateString(),
            end_date: endDate.toLocaleDateString(),
            time: `${startTime} - ${endTime} Please check the call sheet for your final call time`,
            position: positionName,
            kpi: (() => {
              const kpiValue = booking.kpi;
              if (kpiValue === null || kpiValue === undefined || kpiValue === '') {
                return 'N/A';
              }
              
              // Handle objects (like location objects that might be in the wrong field)
              if (typeof kpiValue === 'object' && kpiValue !== null) {
                return 'N/A';
              }
              
              // Convert to number and round to integer
              const numericValue = parseFloat(kpiValue);
              if (!isNaN(numericValue)) {
                return Math.round(numericValue);
              }
              
              // If it's not a valid number, return 'N/A'
              return 'N/A';
            })(),
            client: clientName,
            location: (() => {
              const locationValue = booking.location;
              if (locationValue === null || locationValue === undefined || locationValue === '') {
                return 'N/A';
              }
              
              // Handle location as an object
              if (typeof locationValue === 'object' && locationValue !== null) {
                return locationValue.name || locationValue.short || 'N/A';
              }
              
              // Handle location as a string
              if (typeof locationValue === 'string') {
                return locationValue;
              }
              
              return 'N/A';
            })(),
            additional_info: booking.sonstige_info || ''
          }
        };
 
        await emailService.sendEmail(emailData);
        // console.log(`Confirmation email sent for booking ${booking.id}`);
      } catch (error) {
        console.error('Error sending email notification:', error);
        // Don't throw error here - we want to continue even if email fails
      }
    };
    
    // Create Lano task
    const createTask = async (bookingId, booking, talent) => {
      try {
        // Check if lanoService is available
        if (!lanoService) {
          throw new Error("Lano service not found");
        }
        
        // Get the Lano API token
        const token = await lanoService.getAuthToken();
        
        if (!token) {
          throw new Error("Failed to get Lano API token");
        }

        const kw = 'KW' + getWeekNumber(new Date(booking.start_date)) + " ";
        // Format title with full date instead of seconds
        const startDate = typeof booking.start_date === 'string' ? new Date(booking.start_date) : 
                          booking.start_date && booking.start_date.seconds ? new Date(booking.start_date.seconds * 1000) : 
                          new Date();
                          
        const endDate = typeof booking.end_date === 'string' ? new Date(booking.end_date) : 
                        booking.end_date && booking.end_date.seconds ? new Date(booking.end_date.seconds * 1000) : 
                        startDate;
        
        // Format title using client and position (properly handling objects)
        const clientName = typeof booking.client === 'object' ? (booking.client.label || '') : (booking.client || '');
        const positionName = typeof booking.position === 'object' ? (booking.position.label || '') : (booking.position || '');
        
        // Get talent name from talent object or booking
        const talentName = talent.Name || talent.name || booking.talent_name || 
                          (typeof booking.talent === 'object' ? (booking.talent.name || booking.talent.label) : booking.talent) || '';
        
        // Get location short from booking
        const locationShort = booking.location?.short || 
                             (typeof booking.location === 'string' ? booking.location : '') || '';
                  // Convert dates to YYYY-MM-DD string format for API
        const formattedStartDate = startDate.toISOString().split('T')[0];
        const formattedEndDate = endDate.toISOString().split('T')[0];
        
        // Format dates as dd.mm.yyyy for the title
        const titleStartDate = startDate.toLocaleDateString('de-DE');
        const titleEndDate = endDate.toLocaleDateString('de-DE');
        
        // Construct the title with the correct format including talent name and location
        const title = `${kw}_${clientName}_${positionName}_${talentName}_${titleStartDate}-${titleEndDate}_${locationShort}`;
        
      
        // Debug rate selection
        // console.log('🔍 RATE DEBUG for booking', bookingId, ':', {
          // booking_custom_rate: booking.custom_rate,
          // booking_customRate: booking.customRate,
          // talent_Tagessatz: talent.Tagessatz,
          // allBookingFields: Object.keys(booking),
          // bookingRateFields: Object.keys(booking).filter(key => 
            // key.toLowerCase().includes('rate') || 
            // key.toLowerCase().includes('day') || 
            // key.toLowerCase().includes('custom')
          // ).reduce((obj, key) => {
            // obj[key] = booking[key];
            // return obj;
          // }, {})
        // });
        
        // Extract custom rate value from the structured object
        const customRateValue = booking.custom_rate?.value || booking.customRate;
        const ratecal = customRateValue || talent.Tagessatz;
        // console.log('🔍 FINAL RATE SELECTED:', ratecal);
        // Prepare task data (just the task object, not wrapped)
        const taskData = {
          title: title,
          description: booking.description || ' ',
          details: booking.description || ' ',
          starts_on: formattedStartDate,
          ends_on: formattedEndDate,
          rate: ratecal,
          duration_in_time_units:1,
          currency: "EUR",
          budget_setting: "set_budget",
          assignee_id: talent.Lano_ID,
          rate_type_id: 2,
          creator_id: 220834,
          state: "freelancer_assigned",
          task_cost_centers_attributes: [
            {
              cost_center_1_id: booking.costcenter1?.id,
              cost_center_2_id: booking.costcenter2?.id
            }
          ]
        };
        
        // Debug logging to see what data is being sent
        // console.log('🔍 LANO TASK DATA DEBUG:', {
          // bookingId: bookingId,
          // title: title,
          // rate: ratecal,
          // assignee_id: talent.Lano_ID,
          // starts_on: formattedStartDate,
          // ends_on: formattedEndDate,
         
          // cost_center_1_id: booking.costcenter1?.id,
          // cost_center_2_id: booking.costcenter2?.id,
          // talent: {
            // name: talent.name || talent.Name,
            // lano_id: talent.Lano_ID,
            // tagessatz: talent.Tagessatz
          // },
          // booking: {
            // customRate: booking.customRate,
            // range: booking.range,
            // costcenter1: booking.costcenter1,
            // costcenter2: booking.costcenter2
          // }
        // });
        
        // Call lanoService directly to create task
        await lanoService.createTask(token, taskData);
        // console.log(`Successfully created Lano task for booking ${bookingId}`);
        return true;
      } catch (error) {
        console.error(`Error creating Lano task for booking ${bookingId}:`, error);
        throw error;
      }
    };
    
    // Function to get week number
    function getWeekNumber(d) {
      // Copy date so don't modify original
      d = new Date(Date.UTC(d.getFullYear(), d.getMonth(), d.getDate()));
      // Set to nearest Thursday: current date + 4 - current day number
      // Make Sunday's day number 7
      d.setUTCDate(d.getUTCDate() + 4 - (d.getUTCDay() || 7));
      // Get first day of year
      const yearStart = new Date(Date.UTC(d.getUTCFullYear(), 0, 1));
      // Calculate full weeks to nearest Thursday
      const weekNo = Math.ceil((((d - yearStart) / 86400000) + 1) / 7);
      // Return array of year and week number
      return weekNo;
    }

    // Filter events - removed week filtering to show all confirmed orders
    const filteredEvents = events.filter(event => {
      const searchTermLower = searchTerm.toLowerCase();
      
      const matchesSearchTerm = !searchTerm || (
        (event.boktitle && event.boktitle.toLowerCase().includes(searchTermLower)) ||
        (event.title && event.title.toLowerCase().includes(searchTermLower)) ||
        (event.set && event.set.toLowerCase().includes(searchTermLower)) ||
        (event.talent && event.talent.toLowerCase().includes(searchTermLower)) ||
        (event.status && event.status.toLowerCase().includes(searchTermLower)) ||
        (event.client && event.client.toLowerCase().includes(searchTermLower)) ||
        (event.company && event.company.toLowerCase().includes(searchTermLower))
      );

      // User filtering - check created_by, proposed_by, and processing_by fields
      const matchesUser = !selectedUser || (
        event.created_by === selectedUser.value ||
        event.proposed_by === selectedUser.value ||
        event.proposedBy === selectedUser.value ||
        event.processing_by === selectedUser.value
      );

      return matchesSearchTerm && matchesUser;
    });

    // Sort events to group them together like in NewOrdersList
    const sortedAndFilteredEvents = [...filteredEvents].sort((a, b) => {
      // Get the effective sort date for each item
      const getEffectiveDate = (event) => {
        if (!event.group || event.group === 'No Group') {
          // For non-grouped items, use their individual start date
          return new Date(event.start || 0).getTime();
        } else {
          // For grouped items, use the earliest start date in their group
          const groupEvents = filteredEvents.filter(e => e.group === event.group);
          return Math.min(...groupEvents.map(e => new Date(e.start || 0).getTime()));
        }
      };
      
      const effectiveDateA = getEffectiveDate(a);
      const effectiveDateB = getEffectiveDate(b);
      
      // First sort by effective date (chronological order)
      if (effectiveDateA !== effectiveDateB) {
        return effectiveDateA - effectiveDateB;
      }
      
      // If effective dates are the same, handle grouping
      const groupA = a.group || 'No Group';
      const groupB = b.group || 'No Group';
      
      if (groupA !== groupB) {
        // Different groups with same effective date
        if (groupA === 'No Group' && groupB !== 'No Group') {
          // Single order comes before group when dates are equal
          return -1;
        } else if (groupA !== 'No Group' && groupB === 'No Group') {
          // Group comes after single order when dates are equal
          return 1;
        } else {
          // Both have groups, use group name as tiebreaker
          return groupA.localeCompare(groupB);
        }
      }
      
      // Same group (or both non-grouped), sort by individual start date
      const startDateA = new Date(a.start || 0);
      const startDateB = new Date(b.start || 0);
      return startDateA - startDateB;
    });

    // Create user options from events
    const getUsersFromEvents = () => {
      const users = new Set();
      events.forEach(event => {
        if (event.created_by) users.add(event.created_by);
        if (event.proposed_by) users.add(event.proposed_by);
        if (event.proposedBy) users.add(event.proposedBy);
        if (event.processing_by) users.add(event.processing_by);
      });
      return Array.from(users).filter(user => user && user.trim() !== '')
        .map(user => ({ value: user, label: user }));
    };

    const userOptions = getUsersFromEvents();

    // Helper function to determine if a color is dark or light
    const isColorDark = (hexColor) => {
      if (!hexColor) return false;
      
      // Remove # if present
      const hex = hexColor.replace('#', '');
      
      // Convert to RGB
      const r = parseInt(hex.substr(0, 2), 16);
      const g = parseInt(hex.substr(2, 2), 16);
      const b = parseInt(hex.substr(4, 2), 16);
      
      // Calculate luminance using the formula for relative luminance
      const luminance = (0.299 * r + 0.587 * g + 0.114 * b) / 255;
      
      // Return true if dark (luminance < 0.5)
      return luminance < 0.5;
    };

    // Add helper functions for complex object fields display

    // Helper function to get client display 
    const getClientDisplay = (event) => {
      if (!event) return '';
      
      if (event.client) {
        // Handle client as an object with label property
        if (typeof event.client === 'object' && event.client.label) {
          return event.client.label;
        }
        
        // Handle client as a string
        if (typeof event.client === 'string') {
          return event.client;
        }
      }
      
      return '';
    };

    // Helper function to get position display
    const getPositionDisplay = (event) => {
      if (!event) return '';
      
      if (event.position) {
        // Handle position as an object with label property
        if (typeof event.position === 'object' && event.position.label) {
          return event.position.label;
        }
        
        // Handle position as a string
        if (typeof event.position === 'string') {
          return event.position;
        }
      }
      
      return '';
    };

    // Helper function to get talent display
    const getTalentDisplay = (event) => {
      if (!event) return '';
      
      // For KIND orders (identified by set = "digital Production"), show number of days from title
      if (event.set === "digital Production" && event.title) {
        const match = event.title.match(/(\d+)\s+day/);
        if (match) {
          return `${match[1]} day${match[1] === '1' ? '' : 's'}`;
        }
      }
      
      if (event.talent) {
        // Handle talent as an object with name property
        if (typeof event.talent === 'object' && event.talent.name) {
          return event.talent.name;
        }
        
        // Handle talent as a string
        if (typeof event.talent === 'string') {
          return event.talent;
        }
      }
      
      // Try talent_name field if talent is not available
      if (event.talent_name && typeof event.talent_name === 'string') {
        return event.talent_name;
      }
      
      return '';
    };

    // Helper function to get location display
    const getLocationDisplay = (event) => {
      if (!event) return '';
      
      // Handle location output formatting
      let locationText = '';
      
      // Check if location is an object
      if (event.location && typeof event.location === 'object') {
        // Access name property or short if name doesn't exist
        locationText = event.location.name || event.location.short || '';
      } else if (typeof event.location === 'string') {
        locationText = event.location;
      }
      
      return locationText;
    };

    // Helper function to get location short for filters
    const getLocationShort = (event) => {
      if (!event) return '';
      
      // Handle location as an object with short property
      if (event.location && typeof event.location === 'object') {
        return event.location.short || event.location.name || '';
      } else if (typeof event.location === 'string') {
        return event.location;
      }
      
      // Handle direct location_short property
      if (event.location_short) {
        return event.location_short;
      }
      
      return '';
    };

    // Improved helper to get location information from the event
    const getLocationInfo = (event) => {
      let locationName = '';
      let locationCity = '';
      let locationShort = '';
      
      // Handle location as an object with name/city/short properties
      if (event.location && typeof event.location === 'object') {
        locationName = event.location.name || '';
        locationCity = event.location.city || '';
        locationShort = event.location.short || '';
      }
      
      // Handle direct location properties
      locationName = event.location_name || locationName || (typeof event.location === 'string' ? event.location : '');
      locationCity = event.location_city || locationCity;
      locationShort = event.location_short || locationShort;
      
      return { locationName, locationCity, locationShort };
    };

    // Helper function to get studio display
    const getStudioDisplay = (event) => {
      if (!event) return '';
      
      // Handle studio output formatting
      if (typeof event.studio === 'string') {
        return event.studio;
      } else if (event.studio && typeof event.studio === 'object') {
        return event.studio.name || '';
      }
      
      return '';
    };

    // Helper function for option field
    const getOptionDisplay = (event) => {
      if (!event) return '';
      
      if (event.option) {
        // Handle option as an object with label or value property
        if (typeof event.option === 'object') {
          return event.option.label || event.option.value || '';
        }
        
        // Handle option as a string
        if (typeof event.option === 'string') {
          return event.option;
        }
      }
      
      return '';
    };

    // Helper function for SonstigeInfo field
    const getSonstigeInfoDisplay = (event) => {
      if (!event) return '';
      
      // Check different possible property names for additional info
      if (event.SonstigeInfo) {
        if (typeof event.SonstigeInfo === 'object') {
          return event.SonstigeInfo.text || event.SonstigeInfo.value || '';
        }
        return event.SonstigeInfo;
      }
      
      // Check alternative property names
      if (event.sonstige_info) {
        if (typeof event.sonstige_info === 'object') {
          return event.sonstige_info.text || event.sonstige_info.value || '';
        }
        return event.sonstige_info;
      }
      
      // Check for additional_info property
      if (event.additional_info) {
        if (typeof event.additional_info === 'object') {
          return event.additional_info.text || event.additional_info.value || '';
        }
        return event.additional_info;
      }
      
      return '';
    };

    return (
      <div>
        <div></div>

        <div style={{ marginTop: '40px', marginBottom: '60px' }}>
          <input
            type="text"
            placeholder="Search"
            value={searchTerm}
            onChange={handleChange}
            className='inputtext search'
          />
          
          <div style={{ marginTop: '20px' }}>
            <Select
              value={selectedUser}
              onChange={setSelectedUser}
              options={userOptions}
              isClearable
              placeholder="Filter by User"
              styles={customStyles}
            />
          </div>
        </div>

        <div className='content-wrapper content-wrapper-talent-list'>
          <div className='list-tasks-header'>
            <div style={{ flexBasis: '120px' }}>Group</div>
            <div style={{ flexBasis: '120px' }}>Week</div>
            <div style={{ flexBasis: '120px' }}>Date</div>
            <div style={{ flexBasis: '150px' }}>Client</div>
            <div style={{ flexBasis: '150px' }}>Studio Location</div>
            <div style={{ flexBasis: '100px' }}>Set</div>
            <div style={{ flexBasis: '60px' }}>KPI</div>
            <div style={{ flexBasis: '120px' }}>Position</div>
            <div style={{ flexBasis: '120px' }}>Talent</div>
            <div style={{ flexBasis: '130px' }}>Created</div>
            <div style={{ flexBasis: '120px' }}>S. Info</div>
            <div style={{ flexBasis: '80px' }}></div>
          </div>
        </div>

        {sortedAndFilteredEvents.map((event, index) => {
          const eventDate = new Date(event.start);
          const eventWeek = getWeekNumber(eventDate);
          const startDate = eventDate.toLocaleDateString('en-GB', {
            day: '2-digit',
            month: '2-digit',
            year: 'numeric'
          });
          
          const bookingId = event.bookingId || event.id;
          const isProcessing = processingBookings.has(bookingId);
          const { locationName, locationCity, locationShort } = getLocationInfo(event);
          const studio = event.studio || '';
          const kpi = event.kpi || '';
          const sonstigeInfo = event.sonstige_info || event.SonstigeInfo || event.additional_info || '';
          const talent = event.talent_name || event.talent || '';
          const positionDisplay = getPositionDisplay(event);
          const clientDisplay = getClientDisplay(event);
          const groupId = event.group || '';

          // Get group color from event data
          const groupColor = event.group_color || event.groupColor;

          // Format created_at date and get created_by
          const dateAdded = event.created_at ? new Date(event.created_at).toLocaleDateString('en-GB', {
            day: '2-digit',
            month: '2-digit',
            year: 'numeric'
          }) : 'N/A';
          const createdBy = event.created_by || '';

          return (
            <div 
              className='list-tasks-item'
              key={bookingId || index}
              style={{
                borderLeft: groupColor ? `5px solid ${groupColor}` : 'none',
                paddingLeft: groupColor ? '15px' : '20px'
              }}
            >
              <div style={{ flexBasis: '120px' }}>
                <div 
                  style={{
                    backgroundColor: groupColor || 'transparent',
                    color: isColorDark(groupColor) ? '#ffffff' : '#000000',
                    padding: '4px',
                    borderRadius: '4px',
                    fontWeight: 'bold',
                    textAlign: 'center'
                  }}
                >
                  {groupId}
                </div>
              </div>
              <div style={{ flexBasis: '120px' }}>{eventWeek}</div>
              <div style={{ flexBasis: '120px' }}>{startDate}</div>
              <div style={{ flexBasis: '150px' }}>{clientDisplay}</div>
              <div style={{ flexBasis: '150px' }}>
                {locationShort && studio ? (
                  <>
                    {locationShort} - {studio}
                    {locationCity && <div className="location-city">{locationCity}</div>}
                  </>
                ) : locationShort ? (
                  <>
                    {locationShort}
                    {locationCity && <div className="location-city">{locationCity}</div>}
                  </>
                ) : studio ? (
                  studio
                ) : (
                  ''
                )}
              </div>
              <div style={{ flexBasis: '100px' }}>{event.set}</div>
              <div style={{ flexBasis: '60px' }}>
                {(() => {
                  const kpiValue = kpi;
                  if (kpiValue === null || kpiValue === undefined || kpiValue === '') {
                    return '';
                  }
                  
                  // Handle objects (like location objects that might be in the wrong field)
                  if (typeof kpiValue === 'object' && kpiValue !== null) {
                    // console.warn('KPI is an object, using empty string:', kpiValue);
                    return '';
                  }
                  
                  // Convert to number and round to integer
                  const numericValue = parseFloat(kpiValue);
                  if (!isNaN(numericValue)) {
                    return Math.round(numericValue);
                  }
                  
                  // If it's not a valid number, return empty string
                  return '';
                })()}
              </div>
              <div style={{ flexBasis: '120px' }}>{positionDisplay}</div>
              <div style={{ flexBasis: '120px' }}>{talent}</div>
              <div style={{ flexBasis: '130px' }}>
                {dateAdded}
                {createdBy && (
                  <div className="created-by" title={`Created by: ${createdBy}`}>
                    {createdBy}
                  </div>
                )}
              </div>
              <div style={{ flexBasis: '120px' }}>{sonstigeInfo}</div>
              <div style={{ flexBasis: '80px' }}>
                <a 
                  className={`edit-button ${isProcessing ? 'disabled' : ''}`} 
                  onClick={() => !isProcessing && acceptporposedtalent(bookingId)} 
                  style={{ 
                    opacity: isProcessing ? 0.5 : 1,
                    cursor: isProcessing ? 'not-allowed' : 'pointer'
                  }}
                >
                  <img src={confirmIcon} alt="Accept" className="edit-container" />
                </a>
                <a 
                  className={`edit-button ${isProcessing ? 'disabled' : ''}`} 
                  onClick={() => !isProcessing && declineproposedtalent(bookingId)}
                  style={{ 
                    opacity: isProcessing ? 0.5 : 1,
                    cursor: isProcessing ? 'not-allowed' : 'pointer'
                  }}
                >
                  <img src={declineIcon} alt="Decline" className="edit-container" />
                </a>
              </div>
            </div>
          );
        })}
        <div style={{ marginTop: '80px' }}>
          <p><i>Note: Confirming the booking will create a Lano task and mark the booking as completed.</i></p>
        </div>

        <style jsx>{`
          .location-city {
            display: block;
            font-size: 11px;
            color: #666;
            margin-top: 2px;
          }
          
          .created-by {
            display: block;
            font-size: 11px;
            color: #666;
            margin-top: 2px;
            white-space: nowrap;
            overflow: hidden;
            text-overflow: ellipsis;
          }
        `}</style>
      </div>
    );
};

export default ListViewConfirmed;